// Clojure course content — Part 2 (lessons 12–21)

export const lessonsPart2 = [
  {
    id: "let",
    title: "Local Names with let",
    group: "Working with Functions",
    summary: "Create temporary names inside an expression.",
    content: [
      { t: "p", text: "Sometimes you need a name for an intermediate result, but only inside one calculation. let creates local names that exist only within its body." },
      { t: "code", code: "(let [x 5\n      y 10]\n  (+ x y))" },
      { t: "p", text: "Inside the square brackets you pair each name with a value. Later pairs can use earlier ones, which keeps complex calculations readable." },
      { t: "code", code: "(defn circle-area [r]\n  (let [pi 3.14159\n        r-squared (* r r)]\n    (* pi r-squared)))\n\n(circle-area 10)" },
      { t: "p", text: "Without let you'd have to nest everything into one big expression. let lets you name the steps." },
      { t: "note", text: "These names are local: they vanish once the let finishes. They don't affect anything outside." },
    ],
    exercises: [
      {
        prompt: "Write `hypotenuse` for a right triangle: given legs a and b, return the square root of (a*a + b*b). Use a let for the squares. (js/Math.sqrt computes a square root — the JS way, like you'll see in the interop lesson.)",
        starter: "(defn hypotenuse [a b]\n  (let [aa (* a a)\n        bb (* b b)]\n    ))",
        tests: ["(= (hypotenuse 3 4) 5.0)", "(= (hypotenuse 6 8) 10.0)"],
        solution: "(defn hypotenuse [a b]\n  (let [aa (* a a)\n        bb (* b b)]\n    (js/Math.sqrt (+ aa bb))))",
        hint: "Return (js/Math.sqrt (+ aa bb)).",
      },
      {
        prompt: "Write `discounted` that takes a price and returns it with 20% off, using a let to name the discount amount.",
        starter: "(defn discounted [price]\n  (let [discount (* price 0.2)]\n    ))",
        tests: ["(= (discounted 100) 80.0)", "(= (discounted 50) 40.0)"],
        solution: "(defn discounted [price]\n  (let [discount (* price 0.2)]\n    (- price discount)))",
        hint: "Subtract the discount from the price: (- price discount).",
      },
    ],
  },

  {
    id: "anon",
    title: "Anonymous Functions",
    group: "Working with Functions",
    summary: "Small throwaway functions with fn and #().",
    content: [
      { t: "p", text: "Not every function needs a name. An anonymous function is a function you write inline, usually to hand to another function. The long form uses fn." },
      { t: "code", code: "((fn [x] (* x x)) 6)" },
      { t: "p", text: "That defines a squaring function and immediately calls it with 6. More often you'll pass one along. There's also a short form using #() where % stands for the argument." },
      { t: "code", code: "(#(* % %) 6)" },
      { t: "p", text: "With multiple arguments, use %1, %2, and so on." },
      { t: "code", code: "(#(+ %1 %2) 3 4)" },
      { t: "p", text: "Why bother? Because Clojure has many functions that TAKE functions as input — that's the topic of the next lessons. Anonymous functions let you describe the small operation right where you need it." },
      { t: "note", text: "Both styles create the same kind of function. Use fn when the body is longer or clearer with a name for the parameter; use #() for tiny one-liners." },
    ],
    exercises: [
      {
        prompt: "Using the #() short form, write an anonymous function that adds 10 to its argument, and store it in `add-ten`.",
        starter: "(def add-ten )",
        tests: ["(= (add-ten 5) 15)", "(= (add-ten 0) 10)"],
        solution: "(def add-ten #(+ % 10))",
        hint: "#(+ % 10) — % is the argument.",
      },
      {
        prompt: "Write `apply-twice` that takes a function f and a value x, and applies f to x two times. e.g. (apply-twice inc 5) => 7.",
        starter: "(defn apply-twice [f x]\n  )",
        tests: ["(= (apply-twice inc 5) 7)", "(= (apply-twice #(* % 2) 3) 12)"],
        solution: "(defn apply-twice [f x]\n  (f (f x)))",
        hint: "Call f on x, then call f on that result: (f (f x)).",
      },
    ],
  },

  {
    id: "map-fn",
    title: "Transforming with map",
    group: "Sequence Powers",
    summary: "Apply a function to every element.",
    content: [
      { t: "p", text: "map is one of the most useful functions in Clojure. It takes a function and a collection, and applies the function to every element, giving back a new collection of results." },
      { t: "code", code: "(map inc [1 2 3 4])" },
      { t: "p", text: "Here inc (add one) is applied to each number. The original collection is untouched; you get a new sequence of transformed values." },
      { t: "code", code: "(map #(* % %) [1 2 3 4 5])" },
      { t: "p", text: "The function can be anything — including your own. Transform a list of names to their lengths:" },
      { t: "code", code: "(map count [\"a\" \"bb\" \"ccc\"])" },
      { t: "p", text: "map can even walk two collections at once, pairing them up:" },
      { t: "code", code: "(map + [1 2 3] [10 20 30])" },
      { t: "note", text: "map returns a sequence. Wrap it in (into [] ...) or (vec ...) if you specifically need a vector back." },
    ],
    exercises: [
      {
        prompt: "Write `all-doubled` that doubles every number in a collection. e.g. (all-doubled [1 2 3]) => (2 4 6).",
        starter: "(defn all-doubled [nums]\n  )",
        tests: ["(= (all-doubled [1 2 3]) '(2 4 6))", "(= (all-doubled [10 0 -5]) '(20 0 -10))"],
        solution: "(defn all-doubled [nums]\n  (map #(* % 2) nums))",
        hint: "Use map with #(* % 2).",
      },
      {
        prompt: "Write `lengths` that returns the length of each string in a collection.",
        starter: "(defn lengths [strings]\n  )",
        tests: ["(= (lengths [\"hi\" \"hey\"]) '(2 3))", "(= (lengths [\"\"]) '(0))"],
        solution: "(defn lengths [strings]\n  (map count strings))",
        hint: "map the count function over the strings.",
      },
    ],
  },

  {
    id: "filter",
    title: "Selecting with filter",
    group: "Sequence Powers",
    summary: "Keep only the elements that pass a test.",
    content: [
      { t: "p", text: "Where map transforms every element, filter selects elements. You give it a predicate — a function that returns true or false — and it keeps only the elements for which the predicate is true." },
      { t: "code", code: "(filter even? [1 2 3 4 5 6])" },
      { t: "p", text: "even? returns true for even numbers, so only those survive. You can use any predicate, including anonymous ones." },
      { t: "code", code: "(filter #(> % 3) [1 2 3 4 5])" },
      { t: "p", text: "remove is the mirror image: it drops elements for which the predicate is true (keeping the rest)." },
      { t: "code", code: "(remove even? [1 2 3 4 5 6])" },
      { t: "p", text: "map and filter combine beautifully. First keep the evens, then square them:" },
      { t: "code", code: "(map #(* % %) (filter even? [1 2 3 4 5 6]))" },
      { t: "note", text: "This style — describe WHAT you want (evens, then squared) rather than HOW to loop — is the essence of functional programming." },
    ],
    exercises: [
      {
        prompt: "Write `only-positive` that keeps only the numbers greater than 0.",
        starter: "(defn only-positive [nums]\n  )",
        tests: ["(= (only-positive [-2 3 -1 5 0]) '(3 5))", "(= (only-positive [1 2 3]) '(1 2 3))"],
        solution: "(defn only-positive [nums]\n  (filter pos? nums))",
        hint: "Use filter with pos? (or #(> % 0)).",
      },
      {
        prompt: "Write `long-words` that keeps strings with more than 4 characters.",
        starter: "(defn long-words [words]\n  )",
        tests: ["(= (long-words [\"hi\" \"hello\" \"hey\" \"clojure\"]) '(\"hello\" \"clojure\"))"],
        solution: "(defn long-words [words]\n  (filter #(> (count %) 4) words))",
        hint: "Filter where (> (count %) 4).",
      },
    ],
  },

  {
    id: "reduce",
    title: "Combining with reduce",
    group: "Sequence Powers",
    summary: "Boil a whole collection down to one value.",
    content: [
      { t: "p", text: "reduce takes a collection and combines all its elements into a single value, using a function that merges two things at a time." },
      { t: "code", code: "(reduce + [1 2 3 4 5])" },
      { t: "p", text: "That adds them all: it starts with 1 and 2 to get 3, then adds 3 to get 6, then 4 to get 10, then 5 to get 15. reduce carries the running total along." },
      { t: "p", text: "You can give a starting value as the second argument. This is often clearer and handles empty collections safely." },
      { t: "code", code: "(reduce + 100 [1 2 3])" },
      { t: "p", text: "reduce isn't just for adding. Find the largest number:" },
      { t: "code", code: "(reduce max [3 7 2 9 4])" },
      { t: "p", text: "The combining function receives the running result and the next element. Here we build up a count, ignoring the element, to reimplement count:" },
      { t: "code", code: "(reduce (fn [total _] (inc total)) 0 [:a :b :c])" },
      { t: "note", text: "map, filter and reduce together can express a huge portion of everyday data processing." },
    ],
    exercises: [
      {
        prompt: "Write `sum` that adds all numbers in a collection, returning 0 for an empty collection.",
        starter: "(defn sum [nums]\n  )",
        tests: ["(= (sum [1 2 3 4]) 10)", "(= (sum []) 0)", "(= (sum [5]) 5)"],
        solution: "(defn sum [nums]\n  (reduce + 0 nums))",
        hint: "Use reduce with + and a starting value of 0.",
      },
      {
        prompt: "Write `product` that multiplies all numbers together, starting from 1.",
        starter: "(defn product [nums]\n  )",
        tests: ["(= (product [1 2 3 4]) 24)", "(= (product []) 1)", "(= (product [5 5]) 25)"],
        solution: "(defn product [nums]\n  (reduce * 1 nums))",
        hint: "reduce with * and a starting value of 1.",
      },
      {
        prompt: "Write `largest` that returns the biggest number in a non-empty collection.",
        starter: "(defn largest [nums]\n  )",
        tests: ["(= (largest [3 9 2 7]) 9)", "(= (largest [-5 -2 -9]) -2)"],
        solution: "(defn largest [nums]\n  (reduce max nums))",
        hint: "reduce with the max function.",
      },
    ],
  },

  {
    id: "threading",
    title: "Threading Macros",
    group: "Sequence Powers",
    summary: "Write nested transformations as a readable pipeline.",
    content: [
      { t: "p", text: "When you chain several operations, deeply nested parentheses get hard to read. Threading macros let you write the steps in order, like a pipeline." },
      { t: "p", text: "The thread-first macro -> takes a value and passes it as the FIRST argument to the next call, then feeds that result into the next, and so on." },
      { t: "code", code: "(-> 5\n    (+ 3)\n    (* 2)\n    (- 1))" },
      { t: "p", text: "Read it top to bottom: start with 5, add 3 (=8), multiply by 2 (=16), subtract 1 (=15). Without ->, that would be (- (* (+ 5 3) 2) 1) — same result, harder to read." },
      { t: "p", text: "The thread-last macro ->> passes the value as the LAST argument. This is perfect for sequence functions like map and filter, whose collection is the last argument." },
      { t: "code", code: "(->> (range 10)\n     (filter even?)\n     (map #(* % %))\n     (reduce +))" },
      { t: "p", text: "That reads: take 0–9, keep evens, square them, sum the result. A clean data pipeline." },
      { t: "note", text: "Use -> when the value flows into the first slot (common with maps: assoc, update). Use ->> for sequence pipelines (map, filter, reduce)." },
    ],
    exercises: [
      {
        prompt: "Using ->, start from 10, add 5, then multiply by 3. The result should be 45.",
        starter: "(-> 10\n    )",
        tests: ["(= answer 45)"],
        solution: "(-> 10\n    (+ 5)\n    (* 3))",
        hint: "(-> 10 (+ 5) (* 3)).",
      },
      {
        prompt: "Using ->>, write `sum-of-squares-of-odds` that takes numbers, keeps the odd ones, squares them, and sums the result.",
        starter: "(defn sum-of-squares-of-odds [nums]\n  (->> nums\n       ))",
        tests: ["(= (sum-of-squares-of-odds [1 2 3 4 5]) 35)", "(= (sum-of-squares-of-odds [2 4 6]) 0)"],
        solution: "(defn sum-of-squares-of-odds [nums]\n  (->> nums\n       (filter odd?)\n       (map #(* % %))\n       (reduce +)))",
        hint: "filter odd?, then map square, then reduce +.",
      },
    ],
  },

  {
    id: "recursion",
    title: "Recursion & loop/recur",
    group: "Going Deeper",
    summary: "Solve problems by having a function call itself.",
    content: [
      { t: "p", text: "Recursion means a function that calls itself to work through a problem step by step. Each call handles a smaller piece until it hits a base case that stops the recursion." },
      { t: "code", code: "(defn countdown [n]\n  (when (>= n 0)\n    (println n)\n    (countdown (dec n))))\n\n(countdown 3)" },
      { t: "p", text: "The base case here is when n drops below 0 — then when returns nil and the calls stop. dec subtracts one, shrinking the problem each time." },
      { t: "h", text: "loop / recur" },
      { t: "p", text: "For counting-style loops, Clojure offers loop with recur. loop sets up starting values; recur jumps back to the top with new values. This is Clojure's efficient looping tool." },
      { t: "code", code: "(loop [i 0\n       acc 0]\n  (if (> i 5)\n    acc\n    (recur (inc i) (+ acc i))))" },
      { t: "p", text: "That sums 0+1+2+3+4+5 = 15. loop starts i at 0 and acc at 0; each recur increases i and adds it to acc; when i passes 5 we return acc." },
      { t: "h", text: "Order matters: recur is positional" },
      { t: "p", text: "This is the single most important thing to get right with loop/recur. The values you give to recur are matched to the loop bindings BY POSITION, not by name. In our loop the bindings are [i acc], so recur's first argument becomes the new i and its second becomes the new acc. Read it as two lined-up columns:" },
      { t: "code", code: ";;      i     acc\n(loop [i 0    acc 0]\n  (if (> i 5)\n    acc\n    ;;      new i      new acc\n    (recur (inc i)     (+ acc i))))" },
      { t: "p", text: "If you accidentally swap the order, Clojure will happily run it — there is no error — but your loop will do the wrong thing. Watch what happens when the two arguments are flipped to (recur (+ acc i) (inc i)):" },
      { t: "code", code: ";; BROKEN on purpose — the arguments are in the wrong order!\n(loop [i 0\n       acc 0]\n  (if (> i 5)\n    acc\n    (recur (+ acc i) (inc i))))  ; now i gets the running total, acc gets the counter" },
      { t: "p", text: "Because i now receives the running total, the (> i 5) test trips almost immediately and the answer is completely wrong. The lesson: always make recur's arguments appear in the same order as the loop bindings, and in the same order every time." },
      { t: "note", text: "Two rules for recur: (1) it must be the last thing evaluated (in tail position), and (2) its arguments are POSITIONAL — they must line up, in order, with the loop bindings. Mismatched order is a silent bug, so double-check the columns. This is also how Clojure loops without running out of memory on deep repetition." },
    ],
    exercises: [
      {
        prompt: "Write `factorial` using loop/recur (or recursion). factorial of n is n * (n-1) * ... * 1, and factorial of 0 is 1.",
        starter: "(defn factorial [n]\n  )",
        tests: ["(= (factorial 5) 120)", "(= (factorial 0) 1)", "(= (factorial 1) 1)", "(= (factorial 4) 24)"],
        solution: "(defn factorial [n]\n  (loop [i n\n         acc 1]\n    (if (<= i 1)\n      acc\n      (recur (dec i) (* acc i)))))",
        hint: "Use loop with a counter i and accumulator acc starting at 1; multiply acc by i and decrement i.",
      },
      {
        prompt: "Fix the recur order! This loop should add up the numbers from 1 to n, but the two arguments to recur are swapped, so it returns the wrong answer. The bindings are [i total] — put recur's arguments back in the matching order.",
        starter: "(defn running-sum [n]\n  (loop [i 1\n         total 0]\n    (if (> i n)\n      total\n      ;; the recur arguments are in the WRONG order — swap them back\n      (recur (+ total i) (inc i)))))",
        tests: ["(= (running-sum 5) 15)", "(= (running-sum 1) 1)", "(= (running-sum 10) 55)", "(= (running-sum 3) 6)"],
        solution: "(defn running-sum [n]\n  (loop [i 1\n         total 0]\n    (if (> i n)\n      total\n      (recur (inc i) (+ total i)))))",
        hint: "Bindings are [i total], so recur's first argument becomes the new i (that's (inc i)) and the second becomes the new total (that's (+ total i)).",
      },
      {
        prompt: "Write `sum-to` that adds up all integers from 1 to n (e.g. sum-to 5 = 15).",
        starter: "(defn sum-to [n]\n  )",
        tests: ["(= (sum-to 5) 15)", "(= (sum-to 1) 1)", "(= (sum-to 10) 55)"],
        solution: "(defn sum-to [n]\n  (reduce + (range 1 (inc n))))",
        hint: "You can use (range 1 (inc n)) and reduce +, or a loop/recur.",
      },
    ],
  },

  {
    id: "side-effects",
    title: "Repeating Actions: dotimes & doseq",
    group: "Going Deeper",
    summary: "Loop purely to make things happen — like printing to the screen.",
    content: [
      { t: "p", text: "So far our loops have produced values. But sometimes you loop purely for a side effect — an action that reaches out and changes something, like printing text to the screen. For that, Clojure gives you dotimes and doseq. Both always return nil: you use them for what they DO, not for the value they give back." },
      { t: "h", text: "dotimes: repeat N times" },
      { t: "p", text: "dotimes runs its body a fixed number of times, handing you a counter that goes from 0 up to n-1." },
      { t: "code", code: "(dotimes [i 3]\n  (println \"Hello\" i))" },
      { t: "p", text: "That prints three lines. The [i 3] means 'let i take the values 0, 1, 2 in turn'. Reach for dotimes when you simply need to do something a certain number of times." },
      { t: "h", text: "doseq: do something for each item" },
      { t: "p", text: "doseq walks over a collection and runs its body once per element — think of it as the side-effect cousin of map. It's perfect for printing or otherwise processing each item." },
      { t: "code", code: "(doseq [color [\"red\" \"green\" \"blue\"]]\n  (println \"Color:\" color))" },
      { t: "p", text: "You can bind more than one collection, and doseq loops over every combination, like nested loops:" },
      { t: "code", code: "(doseq [row [1 2]\n        col [:a :b]]\n  (println row col))" },
      { t: "h", text: "Why not just use map?" },
      { t: "p", text: "Remember that map is lazy — it only does its work when something consumes the result, and it builds a sequence you don't actually want here. If you (map println ...) purely for the printing, the work might not even happen. The rule of thumb: use map and filter to TRANSFORM data into new data; use doseq and dotimes when you only care about the side effects." },
      { t: "note", text: "Because dotimes and doseq return nil, they act as a signal to anyone reading your code: 'this is here for its effect, not its value.'" },
    ],
    exercises: [
      {
        prompt: "Write `cheer` that prints the word \"Go!\" on its own line, n times, using dotimes.",
        starter: "(defn cheer [n]\n  )",
        tests: ["(= (with-out-str (cheer 3)) \"Go!\\nGo!\\nGo!\\n\")", "(= (with-out-str (cheer 1)) \"Go!\\n\")", "(= (with-out-str (cheer 0)) \"\")"],
        solution: "(defn cheer [n]\n  (dotimes [i n]\n    (println \"Go!\")))",
        hint: "Use (dotimes [i n] (println \"Go!\")). println adds the newline for you.",
      },
      {
        prompt: "Write `greet-all` that uses doseq to print \"Hi, NAME!\" (one per line) for each name in a collection.",
        starter: "(defn greet-all [names]\n  )",
        tests: ["(= (with-out-str (greet-all [\"Ada\" \"Alan\"])) \"Hi, Ada!\\nHi, Alan!\\n\")", "(= (with-out-str (greet-all [\"Sam\"])) \"Hi, Sam!\\n\")", "(= (with-out-str (greet-all [])) \"\")"],
        solution: "(defn greet-all [names]\n  (doseq [n names]\n    (println (str \"Hi, \" n \"!\"))))",
        hint: "Use (doseq [n names] (println (str \"Hi, \" n \"!\"))).",
      },
    ],
  },

  {
    id: "destructuring",
    title: "Destructuring",
    group: "Going Deeper",
    summary: "Pull values out of collections neatly.",
    content: [
      { t: "p", text: "Destructuring lets you unpack a collection into named pieces right in the binding, instead of pulling each part out by hand. It works in let and in function parameters." },
      { t: "h", text: "Vectors" },
      { t: "code", code: "(let [[a b c] [10 20 30]]\n  (+ a b c))" },
      { t: "p", text: "The names a, b, c line up with positions 0, 1, 2 of the vector. No nth needed." },
      { t: "h", text: "Maps" },
      { t: "p", text: "For maps, use :keys to grab values by their keyword names." },
      { t: "code", code: "(defn describe-person [{:keys [name age]}]\n  (str name \" is \" age))\n\n(describe-person {:name \"Ada\" :age 36})" },
      { t: "p", text: "The function expects a map and immediately unpacks :name and :age into local names. This is extremely common in real Clojure code." },
      { t: "note", text: "Destructuring keeps functions readable: you declare exactly which parts of the data you care about." },
    ],
    exercises: [
      {
        prompt: "Write `sum-pair` that takes a vector of two numbers and returns their sum, using destructuring in the parameter list.",
        starter: "(defn sum-pair [[a b]]\n  )",
        tests: ["(= (sum-pair [3 4]) 7)", "(= (sum-pair [10 -2]) 8)"],
        solution: "(defn sum-pair [[a b]]\n  (+ a b))",
        hint: "The parameter [[a b]] already unpacks the two numbers.",
      },
      {
        prompt: "Write `greeting` that takes a map with :name and :city and returns e.g. \"Hi Ada from London\". Use :keys destructuring.",
        starter: "(defn greeting [{:keys [name city]}]\n  )",
        tests: ["(= (greeting {:name \"Ada\" :city \"London\"}) \"Hi Ada from London\")"],
        solution: "(defn greeting [{:keys [name city]}]\n  (str \"Hi \" name \" from \" city))",
        hint: "Use str to build \"Hi \" name \" from \" city.",
      },
    ],
  },

  {
    id: "state",
    title: "Managing State with atoms",
    group: "Going Deeper",
    summary: "When you truly need something that changes.",
    content: [
      { t: "p", text: "So far all our data has been immutable — values never change. That's wonderful for reasoning about code, but sometimes a program genuinely needs state that changes over time (a score, a counter, a list of items). Clojure's main tool for this is the atom." },
      { t: "p", text: "An atom is a container holding a value. Create one with atom, and read the current value with deref or the shorthand @." },
      { t: "code", code: "(def counter (atom 0))\n@counter" },
      { t: "p", text: "To change it, use swap!, which applies a function to the current value and stores the result." },
      { t: "code", code: "(def counter (atom 0))\n(swap! counter inc)\n(swap! counter inc)\n@counter" },
      { t: "p", text: "swap! can pass extra arguments to the function too:" },
      { t: "code", code: "(def total (atom 0))\n(swap! total + 10)\n(swap! total + 5)\n@total" },
      { t: "p", text: "reset! replaces the value outright, ignoring the old one." },
      { t: "code", code: "(def flag (atom false))\n(reset! flag true)\n@flag" },
      { t: "note", text: "Reach for atoms only when you really need change. Most of your logic should stay in pure, immutable functions — they're easier to test and trust." },
    ],
    exercises: [
      {
        prompt: "Create an atom named `score` starting at 0, then increase it by 3 and by 7 using swap!. The final value should be 10.",
        starter: "(def score (atom 0))\n;; add two swap! calls below\n",
        tests: ["(= @score 10)"],
        solution: "(def score (atom 0))\n(swap! score + 3)\n(swap! score + 7)",
        hint: "(swap! score + 3) then (swap! score + 7).",
      },
      {
        prompt: "Write `make-counter` that returns a function; each time you CALL that function it increases an internal atom by 1 and returns the new value.",
        starter: "(defn make-counter []\n  (let [n (atom 0)]\n    (fn []\n      )))",
        tests: ["(let [c (make-counter)] (c) (c) (= (c) 3))", "(let [c (make-counter)] (= (c) 1))"],
        solution: "(defn make-counter []\n  (let [n (atom 0)]\n    (fn []\n      (swap! n inc))))",
        hint: "The returned function should (swap! n inc), which also returns the new value.",
      },
    ],
  },

  {
    id: "project",
    title: "Capstone: Word Frequencies",
    group: "Advanced Clojure",
    summary: "Combine everything into a real mini-program.",
    content: [
      { t: "p", text: "Time to put the pieces together. We'll count how often each word appears in a sentence — a classic task that uses strings, sequences, maps and reduce." },
      { t: "h", text: "Step 1: split into words" },
      { t: "p", text: "clojure.string/split breaks a string using a pattern. #\" \" is a pattern meaning 'a space'." },
      { t: "code", code: "(require '[clojure.string :as str])\n(str/split \"the cat sat\" #\" \")" },
      { t: "h", text: "Step 2: count with frequencies" },
      { t: "p", text: "Clojure has a built-in for exactly this: frequencies returns a map of each item to how many times it appears." },
      { t: "code", code: "(frequencies [\"a\" \"b\" \"a\" \"c\" \"a\"])" },
      { t: "h", text: "Step 3: put it together" },
      { t: "code", code: "(require '[clojure.string :as str])\n(defn word-count [sentence]\n  (frequencies (str/split sentence #\" \")))\n\n(word-count \"the cat sat on the mat\")" },
      { t: "p", text: "In just a couple of lines you built a genuinely useful function. Notice the style: each small piece does one thing, and they compose. That's Clojure." },
      { t: "note", text: "You've reached the end of the course. Revisit the REPL playground to keep experimenting — that's how Clojure really sinks in. Congratulations!" },
    ],
    exercises: [
      {
        prompt: "Write `count-words` that returns how many words are in a sentence (words separated by single spaces).",
        starter: "(require '[clojure.string :as str])\n\n(defn count-words [sentence]\n  )",
        tests: ["(= (count-words \"hello world\") 2)", "(= (count-words \"one two three four\") 4)"],
        solution: "(require '[clojure.string :as str])\n\n(defn count-words [sentence]\n  (count (str/split sentence #\" \")))",
        hint: "Split on #\" \" then count the resulting pieces.",
      },
      {
        prompt: "Write `most-common-count` that returns the highest frequency of any word in a sentence. e.g. \"a b a c a\" => 3 (the word \"a\").",
        starter: "(require '[clojure.string :as str])\n\n(defn most-common-count [sentence]\n  )",
        tests: ["(= (most-common-count \"a b a c a\") 3)", "(= (most-common-count \"one two two\") 2)"],
        solution: "(require '[clojure.string :as str])\n\n(defn most-common-count [sentence]\n  (->> (str/split sentence #\" \")\n       frequencies\n       vals\n       (reduce max)))",
        hint: "Split, take frequencies, then find the max of its vals.",
      },
    ],
  },
];
